import {
  DATE_RANGE_KEY,
  FORECAST_KEY,
  PDEN_SOURCE_KEY,
  SYNC_KEY
} from "constants/settings.constants";
import { toPdenEnum } from "utils/toPdenEnum";

import { IGroupBy, PerformanceDateRange } from "models/groupBy";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

const DATE_RANGE_MARKER = `.${DATE_RANGE_KEY}.`;
const PDEN_SOURCE_MARKER = `.${PDEN_SOURCE_KEY}.`;

// The enum's numbers alone. A numeric enum holds its names as well, each mapping back to its
// number, so a membership test taken straight off the object accepts "gDC" as readily as 0.
const PDEN_SOURCE_VALUES = new Set<number>(
  Object.values(PdenSourceEnum).filter(
    (value): value is number => typeof value === "number"
  )
);

/** The date range and production source a stored tile spells out, when it spells them out. */
export interface TileQualifications {
  performanceDateRange?: PerformanceDateRange;
  /** The production source as the enum's numeric value. */
  pdenSource?: number;
}

/**
 * Adds `.dr.<startDate>.<endDate>` and `.pden.<source>` to a property, in front of the forecast
 * or sync marker when the property already carries one.
 *
 * The forecast marker has to stay last. parseForecastQualification reads everything after
 * `.fcst` as the source and rescat, so segments behind it would be taken for a forecast source
 * and answer from the wrong forecast without a word.
 *
 * The range goes ahead of the source, always, so the same tile is spelled one way and reads
 * back the way it was written.
 *
 * Returns the property untouched when there is neither, which is what keeps a field with no
 * range and no source of its own spelled exactly as it always was.
 */
export function qualifyTileProperty(
  property: string,
  performanceDateRange: Maybe<PerformanceDateRange>,
  pdenSource?: Maybe<number>
): string {
  const dateRangeSuffix =
    performanceDateRange?.startDate && performanceDateRange?.endDate
      ? `${DATE_RANGE_MARKER}${performanceDateRange.startDate}.${performanceDateRange.endDate}`
      : "";
  // Compared against null rather than tested for truth: gDC is 0, so a truthy test would
  // leave a gDC tile with no source at all and send it back to borrowing the global one.
  const pdenSourceSuffix = pdenSource != null ? `${PDEN_SOURCE_MARKER}${pdenSource}` : "";
  const suffix = `${dateRangeSuffix}${pdenSourceSuffix}`;

  if (!suffix) {
    return property;
  }

  const markerIndexes = [`.${FORECAST_KEY}`, `.${SYNC_KEY}`]
    .map((marker) => property.indexOf(marker))
    .filter((index) => index >= 0);

  if (!markerIndexes.length) {
    return `${property}${suffix}`;
  }

  const insertAt = Math.min(...markerIndexes);
  return `${property.slice(0, insertAt)}${suffix}${property.slice(insertAt)}`;
}

/**
 * Reads the range and the source back out of a property — the inverse of qualifyTileProperty,
 * which is why it lives next to it.
 *
 * One function for both, rather than one each: two readers of the same string are free to
 * disagree about where each marker sits, and the tile is the only record of either.
 *
 * Only the segments belonging to each marker are read, so the forecast tail that follows them
 * is left alone. Undefined for a tile stored before either was kept, so those tiles carry on
 * borrowing whatever the session holds.
 */
export function parseTileQualifications(property: Maybe<string>): TileQualifications {
  return {
    performanceDateRange: readDateRange(property),
    pdenSource: readPdenSource(property)
  };
}

function readDateRange(property: Maybe<string>): PerformanceDateRange | undefined {
  const markerIndex = property?.indexOf(DATE_RANGE_MARKER) ?? -1;
  if (markerIndex < 0) {
    return undefined;
  }

  const [startDate, endDate] = property
    .slice(markerIndex + DATE_RANGE_MARKER.length)
    .split(".");
  return startDate && endDate ? { startDate, endDate } : undefined;
}

function readPdenSource(property: Maybe<string>): number | undefined {
  const markerIndex = property?.indexOf(PDEN_SOURCE_MARKER) ?? -1;
  if (markerIndex < 0) {
    return undefined;
  }

  const [segment] = property.slice(markerIndex + PDEN_SOURCE_MARKER.length).split(".");
  const source = Number(segment);
  // The empty segment is checked before the number is trusted, because Number("") is 0 and 0
  // is gDC — so a marker with nothing behind it would otherwise read as a real source.
  //
  // Only a number the enum still names is handed back. A tile is stored by the user service and
  // outlives the release that wrote it, so a retired source, a typo or a fraction would go on
  // being asked for indefinitely; dropping it sends the tile back to the global source instead.
  return segment.length && PDEN_SOURCE_VALUES.has(source) ? source : undefined;
}

/**
 * Cuts every qualification that is ours alone out of the middle of a property and joins what is
 * left, so `Performance.Gas.Cum.dr.20210901.20241031.pden.5.fcst.P+PDP` comes back as
 * `Performance.Gas.Cum.fcst.P+PDP`.
 *
 * The range and the production source are ours alone: the service is sent the property as it is
 * written and reads `.fcst` and `.sync` out of it, but knows nothing about `.dr` or `.pden`. So
 * either may sit in a stored tile, and both have to be taken back out before the property is
 * used for anything else.
 *
 * One function for both, so that everything keyed on a stripped property — the session ranges,
 * the local bins, the analytics event — agrees on what a stripped property is.
 *
 * formatPropertyName is not this. It truncates at the first marker it finds, which is right
 * when the bare column name is wanted and wrong here, because the forecast tail behind the
 * qualifications still names the source and rescat the request needs.
 */
export function stripTileQualifications(property: string): string {
  return cutQualification(
    cutQualification(property, DATE_RANGE_MARKER, 2),
    PDEN_SOURCE_MARKER,
    1
  );
}

function cutQualification(
  property: string,
  marker: string,
  segmentCount: number
): string {
  const markerIndex = property?.indexOf(marker) ?? -1;
  if (markerIndex < 0) {
    return property;
  }

  const tail = property
    .slice(markerIndex + marker.length)
    .split(".")
    .slice(segmentCount)
    .join(".");
  const head = property.slice(0, markerIndex);
  return tail ? `${head}.${tail}` : head;
}

/**
 * Whether a stored tile names the focus that is committed now.
 *
 * The committed property has the range and the source cut back out of it, so the tile has to be
 * stripped the same way before the two can be compared at all.
 *
 * Each is compared only when the tile carries it. A field can be stored as two tiles that
 * differ by their dates or their source alone, and both strip to the same property, so those
 * are all that tell them apart. A tile stored before either was kept has neither, and it still
 * has to match a focus that has them: a focus picks up whatever the session holds for its
 * field, so asking that tile for a range or a source it never had would leave it unlit from
 * the first one used in the tab.
 *
 * The stored source is read back as a number already, while a committed focus can hold either a
 * number or a name such as "Public", so only the focus side goes through toPdenEnum.
 *
 * toPdenEnum answers Public for a focus carrying no source at all, so a tile stored on Public
 * matches one. That holds only because updateGroupBy gives every focus it commits a source and
 * defaults it to "Public" — the assumption sits a function away, so move that default and this
 * match moves with it.
 */
export function doesTileMatchFocus(
  tile: string,
  focus: Maybe<Pick<IGroupBy, "property" | "performanceDateRange" | "pdenSource">>
): boolean {
  const { performanceDateRange: tileDateRange, pdenSource: tilePdenSource } =
    parseTileQualifications(tile);
  return (
    focus?.property === stripTileQualifications(tile) &&
    (!tileDateRange ||
      (tileDateRange.startDate === focus?.performanceDateRange?.startDate &&
        tileDateRange.endDate === focus?.performanceDateRange?.endDate)) &&
    // Compared against null rather than tested for truth: gDC is 0, so a truthy test would
    // stop comparing the source for a gDC tile and light it up beside a tile for any other
    // source on the same field.
    (tilePdenSource == null || tilePdenSource === toPdenEnum(focus?.pdenSource))
  );
}
